import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import {
  initializeGoogleOneTap,
  promptGoogleOneTap,
  disableAutoSelect,
  revokeGoogleSession,
  isGoogleAuthConfigured,
} from '../services/googleOneTap';
import { exchangeGoogleCredential, refreshTokens, AuthUser } from '../services/authApi';
import { setAuthToken } from '../services/api';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  signIn: () => void;
  signOut: () => void;
  // For gradual rollout: allow unauthenticated access
  authEnabled: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_STORAGE_KEY = 'stc_auth';

interface StoredAuth {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: AuthUser;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  const authEnabled = isGoogleAuthConfigured();

  // Sign out function
  const signOut = useCallback(async () => {
    if (user?.email) {
      await revokeGoogleSession(user.email);
    }
    setUser(null);
    setIdToken(null);
    setAccessToken(null);
    setRefreshToken(null);
    setExpiresAt(0);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    disableAutoSelect();
    // Clear API auth token
    setAuthToken(null);
  }, [user]);

  // Refresh tokens before expiration
  const doRefresh = useCallback(
    async (currentRefreshToken: string) => {
      try {
        const response = await refreshTokens(currentRefreshToken);

        const newExpiresAt = Date.now() + response.expiresIn * 1000;

        setIdToken(response.idToken);
        setExpiresAt(newExpiresAt);

        // Update localStorage
        const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (stored) {
          const parsed: StoredAuth = JSON.parse(stored);
          parsed.idToken = response.idToken;
          parsed.accessToken = response.accessToken;
          parsed.expiresAt = newExpiresAt;
          localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(parsed));
          // Update API auth token
          setAuthToken(response.idToken);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Force re-login
        signOut();
      }
    },
    [signOut]
  );

  // Handle Google credential from One Tap
  const handleGoogleCredential = useCallback(async (credential: string) => {
    try {
      setIsLoading(true);

      const response = await exchangeGoogleCredential(credential);

      const tokenExpiresAt = Date.now() + response.expiresIn * 1000;

      // Store in state
      setUser(response.user);
      setIdToken(response.idToken);
      setAccessToken(response.accessToken);
      setRefreshToken(response.refreshToken);
      setExpiresAt(tokenExpiresAt);

      // Persist to localStorage
      const stored: StoredAuth = {
        idToken: response.idToken,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresAt: tokenExpiresAt,
        user: response.user,
      };
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(stored));

      // Set API auth token for subsequent calls
      setAuthToken(response.idToken);

      console.log('Authentication successful for:', response.user.email);
    } catch (error) {
      console.error('Authentication failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize from localStorage and Google One Tap
  // Only run once on mount - authEnabled is stable (derived from env var)
  useEffect(() => {
    const init = async () => {
      // Initialize Google One Tap if configured
      if (authEnabled) {
        initializeGoogleOneTap(handleGoogleCredential);
      }

      // Check for existing session
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);

      if (stored) {
        try {
          const parsed: StoredAuth = JSON.parse(stored);

          // Check if token is expired or expiring soon (5 min buffer)
          if (parsed.expiresAt > Date.now() + 5 * 60 * 1000) {
            // Token still valid
            setUser(parsed.user);
            setIdToken(parsed.idToken);
            setAccessToken(parsed.accessToken);
            setRefreshToken(parsed.refreshToken);
            setExpiresAt(parsed.expiresAt);
            // Set API auth token
            setAuthToken(parsed.idToken);
          } else if (parsed.refreshToken) {
            // Token expired, try refresh
            setUser(parsed.user);
            setRefreshToken(parsed.refreshToken);
            await doRefresh(parsed.refreshToken);
          }
        } catch (error) {
          console.error('Session restore failed:', error);
          localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
      }

      setIsLoading(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Run only on mount; callbacks are stable via useCallback
  }, [authEnabled]);

  // Auto-prompt One Tap for unauthenticated users (after a short delay)
  useEffect(() => {
    if (!isLoading && !user && authEnabled) {
      const timer = setTimeout(() => {
        promptGoogleOneTap();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isLoading, user, authEnabled]);

  // Schedule token refresh before expiration
  useEffect(() => {
    if (!refreshToken || !expiresAt) return;

    const timeUntilExpiry = expiresAt - Date.now();
    const refreshIn = timeUntilExpiry - 5 * 60 * 1000; // 5 min before expiry

    if (refreshIn <= 0) {
      doRefresh(refreshToken);
      return;
    }

    const timer = setTimeout(() => {
      doRefresh(refreshToken);
    }, refreshIn);

    return () => clearTimeout(timer);
  }, [refreshToken, expiresAt, doRefresh]);

  const signIn = useCallback(() => {
    if (authEnabled) {
      promptGoogleOneTap();
    }
  }, [authEnabled]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        idToken,
        accessToken,
        refreshToken,
        signIn,
        signOut,
        authEnabled,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
